/**
 * FAQ — public FAQ page. Linked from landing footer.
 * Answers the questions restaurant owners actually ask during pitch calls.
 */
import { Link } from "react-router-dom";
import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";

const FAQS = [
  {
    q: "What will I actually spend? Give me the real total between BuzzerAI + Twilio, no marketing math.",
    a: (
      <>
        <p><b>You'll pay two bills: ours and Twilio's.</b> We take zero markup on Twilio — you pay Twilio directly for the phone line, calls, and texts. Here's every dollar, honestly:</p>

        <p className="font-heading font-bold text-[#0E1512] mt-4 mb-1">🐝 Your BuzzerAI bill (one flat monthly amount)</p>
        <ul>
          <li><b>Free forever tier:</b> $0. Waitlist only, no phone AI, no SMS alerts.</li>
          <li><b>Essential:</b> $29/month. AI phone reception + chat widget.</li>
          <li><b>Complete (most restaurants pick this):</b> $49/month. AI phone + chat + smart waitlist + walk-in tracking.</li>
          <li><b>Multi-Location:</b> $99/month. Everything, unlimited locations.</li>
          <li><b>Annual billing:</b> 2 months free (Complete = $490/year instead of $588).</li>
          <li><b>Cancel:</b> one tap in Settings, any day. No contract, no exit fee.</li>
        </ul>

        <p className="font-heading font-bold text-[#0E1512] mt-4 mb-1">📞 Your Twilio bill (variable, paid directly to Twilio)</p>
        <p>Twilio is the telecom backbone that owns your phone number and delivers your SMS. You pay them the going rate — we don't touch these dollars.</p>
        <ul>
          <li><b>Phone number rental:</b> $1.15/month (US local number)</li>
          <li><b>Inbound calls (Mimi answering):</b> $0.0085 per minute</li>
          <li><b>Outbound SMS (table-ready alerts):</b> $0.0079 per message</li>
          <li><b>Inbound SMS (guest replies like CANCEL):</b> $0.0075 per message</li>
          <li><b>A2P 10DLC campaign fee:</b> ~$10/month (US carrier compliance — required to send business SMS, non-negotiable, applies to everyone in this space)</li>
        </ul>
        <p className="text-xs text-[#5C6B61] italic mt-1">One-time Twilio setup fees: A2P brand registration is $4 (sole prop) to $44 (LLC), paid once at signup to The Campaign Registry via Twilio. That's it.</p>

        <p className="font-heading font-bold text-[#0E1512] mt-4 mb-1">💰 What that adds up to for a real indie restaurant</p>
        <div className="my-3 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#F7F6F2] text-left">
                <th className="border border-[#E1DFD5] px-3 py-2">Traffic level</th>
                <th className="border border-[#E1DFD5] px-3 py-2">Twilio / mo</th>
                <th className="border border-[#E1DFD5] px-3 py-2">BuzzerAI Complete</th>
                <th className="border border-[#E1DFD5] px-3 py-2"><b>Total / mo</b></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-[#E1DFD5] px-3 py-2">Slow (5 calls/day, 5 SMS/day)</td>
                <td className="border border-[#E1DFD5] px-3 py-2">~$16</td>
                <td className="border border-[#E1DFD5] px-3 py-2">$49</td>
                <td className="border border-[#E1DFD5] px-3 py-2 font-bold text-[#1E3A2F]">~$65</td>
              </tr>
              <tr className="bg-[#F7F6F2]/50">
                <td className="border border-[#E1DFD5] px-3 py-2"><b>Typical dinner service</b> (12 calls/day, 15 SMS/day)</td>
                <td className="border border-[#E1DFD5] px-3 py-2">~$22</td>
                <td className="border border-[#E1DFD5] px-3 py-2">$49</td>
                <td className="border border-[#E1DFD5] px-3 py-2 font-bold text-[#1E3A2F]">~$71</td>
              </tr>
              <tr>
                <td className="border border-[#E1DFD5] px-3 py-2">Busy (25 calls/day, 30 SMS/day)</td>
                <td className="border border-[#E1DFD5] px-3 py-2">~$32</td>
                <td className="border border-[#E1DFD5] px-3 py-2">$49</td>
                <td className="border border-[#E1DFD5] px-3 py-2 font-bold text-[#1E3A2F]">~$81</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#5C6B61] italic">Ranges are typical, not guarantees. Twilio bills the actual minute and message count.</p>

        <p className="font-heading font-bold text-[#0E1512] mt-4 mb-1">🧾 One-time optional charges (not required)</p>
        <ul>
          <li><b>White-Glove SMS Setup:</b> $40 one-time. We fill out the entire A2P compliance paperwork inside Twilio for you. Skip this if you're comfortable using our in-app wizard yourself (takes about 15 minutes).</li>
        </ul>

        <p className="font-heading font-bold text-[#0E1512] mt-4 mb-1">🚫 What we don't charge you for</p>
        <ul>
          <li>No setup fee for BuzzerAI itself</li>
          <li>No per-call or per-SMS markup on top of what Twilio charges you</li>
          <li>No per-cover fees (OpenTable-style)</li>
          <li>No mandatory annual contract</li>
          <li>No sales calls to cancel</li>
        </ul>

        <p className="mt-4"><b>Bottom line:</b> for the typical indie restaurant, budget <b className="text-[#1E3A2F]">~$70–90/month all-in</b> for a fully-loaded AI phone receptionist + smart waitlist + SMS. Compare that to OpenTable ($250+ setup plus per-cover fees), a part-time host ($2,400/month plus tips), or enterprise voice AI like SoundHound ($2,000+/month). We're transparent about every line so there are no ugly surprises 90 days in.</p>
      </>
    ),
  },
  {
    q: "Am I turning my restaurant over to an AI? That feels risky.",
    a: (
      <>
        <p>Legitimate concern, and one we hear from every operator on the first call. Short answer: <b>no, you're not.</b> Here's what actually happens:</p>
        <p><b>1. Your phone rings first, always.</b> The AI only picks up unanswered calls. If you or your staff answer in 20 seconds, the AI never enters the conversation. It's not replacing anyone — it's catching what would otherwise ring out.</p>
        <p><b>2. Every call is transcribed and reviewable.</b> Read what the AI said. Read what the caller said. Read every SMS it sent. Nothing is hidden from you. Most owners spot-check the first 15–20 calls and then relax. If you spot something you don't love, edit the AI's greeting and it changes on the next call.</p>
        <p><b>3. One-tap kill switch.</b> Flip the AI off in Settings any time — for one shift, one day, or forever. Calls fall back to your voicemail like they did before you signed up. No contract, no penalty. You can turn it back on the same way.</p>
        <p><b>4. Human handoff, every time it matters.</b> The manager gets an SMS the moment a caller asks for a person, wants to book a future date, or the AI hits something it can't handle. Nothing gets stranded with the AI. You decide what happens next.</p>
        <p><b>5. No AI decision you can't reverse.</b> Every guest the AI adds is in your dashboard. Remove any of them with one tap if the AI misread something. You are always the source of truth.</p>
        <p>The trust build we recommend: turn the AI on, don't tell anyone yet, and watch the first 20 calls. When you see it politely add a party of 4 for Friday night at 7:15 while you're in the middle of a Saturday rush — that's the moment the fear melts. It's the same moment your best host would've handled that call, except this one doesn't call in sick.</p>
      </>
    ),
  },
  {
    q: "What happens if our internet goes down?",
    a: (
      <>
        <p><b>Your phones still work, and your dashboard still shows the current queue.</b> This is one of the strongest reasons to use BuzzerAI over a VoIP-only setup, and it's specifically true for the way most of our restaurants are set up: your existing cell phone stays your "house phone," and it forwards to BuzzerAI when unanswered.</p>
        <p><b>Here's the actual path a caller takes:</b></p>
        <ol>
          <li>Guest dials your restaurant's phone number (which is your existing cell number).</li>
          <li>Your cell rings on the <b>cellular network</b> — that's Verizon/AT&T/T-Mobile, not your restaurant's WiFi. It rings whether or not your Comcast is up.</li>
          <li>If nobody picks up in ~20 seconds, your carrier auto-forwards the call to the BuzzerAI Twilio number.</li>
          <li>Twilio routes to BuzzerAI in the cloud. The AI answers, adds the guest to the waitlist, and texts them the confirmation.</li>
        </ol>
        <p><b>Your restaurant's internet is not touched at any point in this flow.</b> It doesn't matter if your WiFi is up or down.</p>
        <p><b>What about seeing the waitlist itself?</b> The BuzzerAI dashboard caches your live queue in the browser every 15 seconds. If your WiFi drops mid-shift, the dashboard keeps showing the last known queue with a coral "You're offline" banner at the top. You still see who's next, who's been waiting longest, and every guest's phone number. You can't send SMS or mark seatings until the connection returns, but you're never blind.</p>
        <p>What <em>does</em> stop working during a WiFi outage:</p>
        <ul>
          <li>Sending SMS notifications from the dashboard (the AI's automatic ones still fire because they're triggered in the cloud)</li>
          <li>Marking guests as Seated / Notified / Removed until you reconnect</li>
          <li>Settings changes</li>
        </ul>
        <p>What <b>keeps working</b> during a WiFi outage:</p>
        <ul>
          <li>The AI answers every forwarded phone call</li>
          <li><b>The host dashboard keeps showing the last-known queue</b> (cached locally)</li>
          <li>Guests joining via the public /join page (they use their own cellular data)</li>
          <li>SMS-when-ready alerts to guests that the AI adds itself</li>
          <li>Manager transfers (Twilio dials your manager's cell — cellular, not WiFi)</li>
          <li>Voicemails</li>
          <li>Push alerts to the host phone (delivered over cellular)</li>
        </ul>
        <p><b>Recovery playbook</b>: The host tethers their phone as a hotspot and the dashboard reconnects in seconds. Zero calls are lost during the outage — they're all caught by the AI, and the cached queue means the host is never guessing who's next.</p>
      </>
    ),
  },
  {
    q: "My \"house phone\" is my personal cell — how does BuzzerAI fit in?",
    a: (
      <>
        <p>This is the setup most of our restaurants use. You keep your existing cell number as the restaurant's number, and you turn on <b>conditional call forwarding</b> so unanswered calls forward to BuzzerAI. It takes about 90 seconds to set up.</p>
        <p><b>How it works:</b></p>
        <ol>
          <li>Your cell rings first — you or your staff have the first crack at picking up.</li>
          <li>If nobody answers within ~20 seconds (or the cell is busy, off, or out of signal), your carrier automatically forwards the call to BuzzerAI's Twilio number.</li>
          <li>BuzzerAI's AI answers, adds the caller to the waitlist, and texts them.</li>
        </ol>
        <p><b>How to enable it (one-time, ~90 seconds):</b></p>
        <ul>
          <li><b>Verizon</b>: Dial <code>*71</code> + the BuzzerAI number + call. To disable: <code>*73</code>.</li>
          <li><b>AT&T</b>: Dial <code>*004*</code> + the BuzzerAI number + <code>#</code> + call. To disable: <code>##004#</code>.</li>
          <li><b>T-Mobile</b>: Dial <code>**61*</code> + the BuzzerAI number + <code>#</code> + call. To disable: <code>##61#</code>.</li>
          <li><b>Any phone</b>: Settings → Phone → Call Forwarding → "When Unanswered" → enter BuzzerAI number.</li>
        </ul>
        <p><b>Important</b>: use <em>conditional</em> forwarding (only forward when unanswered), never <em>unconditional</em> forwarding (<code>*72</code> on most carriers). Unconditional forwards <b>every</b> call to the AI — your cell won't ring at all.</p>
        <p><b>Your personal contacts, iMessage, and personal calls are unaffected.</b> BuzzerAI only handles calls that would otherwise ring out and be missed.</p>
        <p>If your cell is off, dead battery, or out of signal → carriers still forward directly to BuzzerAI, so the AI still catches those calls. If <em>you</em> answer, it's a normal call and the AI never sees it.</p>
      </>
    ),
  },
  {
    q: "What if the AI doesn't understand a caller?",
    a: (
      <>
        <p>The AI is trained to ask a clarifying question. If it still can't understand, it transfers the caller to the manager on duty (their cell phone). Nothing ends in a dead end.</p>
        <p>Every unclear call is logged with a transcript so you can review what went wrong and tighten the AI's greeting for your restaurant specifically.</p>
      </>
    ),
  },
  {
    q: "Can I keep my existing restaurant phone number?",
    a: (
      <>
        <p>Yes — and this is the setup we recommend. See the "my house phone is my personal cell" question above for the exact steps.</p>
        <p>Two approaches to keep your number:</p>
        <p><b>Simple (recommended)</b>: keep your existing number, and set up <b>conditional call forwarding</b> on it. When someone calls your restaurant and nobody picks up in 20 seconds, the call auto-forwards to your BuzzerAI Twilio number. Setup takes 2 minutes and works on any US carrier. You never lose control of your number.</p>
        <p><b>Full port</b>: transfer your existing number into Twilio so every call goes straight to BuzzerAI without ringing your cell first. Takes 3–7 business days for the port to complete. Only recommended if you want the AI to be your <em>primary</em> line and don't need your cell to ring first.</p>
        <p>Our onboarding copilot walks you through either path in ~5 minutes.</p>
      </>
    ),
  },
  {
    q: "What if a guest asks the AI to book a table for a future date?",
    a: (
      <>
        <p>Important framing first: <b>BuzzerAI is not a reservation system.</b> We don't hold tables in advance. What we do is smart waitlist placement — a "priority list-add" ahead of service.</p>
        <p>When a caller says "put me down for Friday at 7," the AI politely explains: <em>"I can put your name at the top of the list for that time so you're first in line when service opens — I just can't hold a specific table."</em> If they agree, it captures the request. The caller gets a text confirming they're on the priority list (not that a table is set aside). You get a text with the details so you can follow up.</p>
        <p>Every request lands in your dashboard's <b>Priority waitlist requests</b> panel. When service starts, you decide the order to work through them — the AI just makes sure nobody who called ahead gets forgotten.</p>
        <p>Over time this data aggregates — if 20 people ask for Friday 7pm, that's a strong staffing signal (not a booking obligation).</p>
      </>
    ),
  },
  {
    q: "Is guest data secure? Where is it stored?",
    a: (
      <>
        <p>Guest data (name, phone, party size, notes) is stored in an encrypted MongoDB database. We automatically redact personally identifiable information after 90 days — you keep the analytics (party sizes, wait times) but the phone numbers and names are wiped. Nothing sits around forever.</p>
        <p>Nobody outside your account can see your guests. There's no data sold, no ads, no third-party sharing. Just your restaurant, your list.</p>
      </>
    ),
  },
  {
    q: "Can I cancel? Am I locked into a contract?",
    a: (
      <>
        <p>Cancel any time, right from your settings page. No contract, no cancellation fee, no "talk to sales" nonsense. Your data stays available for 30 days after cancellation in case you want to come back or export it.</p>
        <p>The free waitlist tier stays free forever — even if you never pay a cent, you can keep using it.</p>
      </>
    ),
  },
  {
    q: "Does this work for coffee shops / bars / bakeries — not just sit-down restaurants?",
    a: (
      <>
        <p>Yes. The waitlist and AI receptionist work across hospitality — coffee shops love the SMS-when-ready for to-go, bars use it for peak-hour tables, and appointment-based businesses (salons, clinics, tutors) can turn on our Scheduling add-on for calendars.</p>
        <p><b>We do not do restaurant reservations by design.</b> Reservations create no-shows and phantom tables. Our smart waitlist + book-ahead capture is the honest way to run a restaurant floor — first-come, first-seated, with SMS the moment a table opens.</p>
      </>
    ),
  },
  {
    q: "What if I have multiple locations?",
    a: (
      <>
        <p>Multi-location is built in. Each location gets its own waitlist, Twilio number, staff logins, and analytics. Switch between them from a dropdown in the sidebar. Owner sees everything, per-location managers see just theirs.</p>
        <p>Pricing scales with locations — talk to us if you're running more than 3 and we'll work something out.</p>
      </>
    ),
  },
  {
    q: "How is this different from Waitwhile / OpenTable / Toast?",
    a: (
      <>
        <p><b>Waitwhile</b>: Great waitlist, no AI receptionist, no phone reception. $89–200/mo.</p>
        <p><b>OpenTable / Resy</b>: Reservation-first, expensive, charges per seated guest. Doesn't help with walk-ins or missed calls.</p>
        <p><b>Toast</b>: POS-first with a bundled waitlist add-on. Ties you to their POS ecosystem.</p>
        <p><b>BuzzerAI</b>: Free waitlist tier + tiered plans from $29-99/mo (Essential / Complete / Multi-Location). Purpose-built for independent restaurants. Bring your own Twilio (no reseller markup). Works alongside any POS.</p>
      </>
    ),
  },
];

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="paper-card overflow-hidden" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 hover:bg-[#E6E2D6]/40 transition-colors"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-heading font-extrabold text-lg text-[#121914]">{q}</span>
        <CaretDown
          size={20}
          weight="bold"
          color="#1E3A2F"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms" }}
        />
      </button>
      {open && (
        <div className="px-6 pb-6 space-y-3 text-[#5C6B61] leading-relaxed text-sm border-t border-[#E1DFD5] pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      {/* Slim top bar */}
      <div className="border-b border-[#E1DFD5] bg-white/60 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="faq-home-link">
            <div className="w-8 h-8 rounded-lg bg-[#1E3A2F] flex items-center justify-center">
              <BuzzLogo size={22} />
            </div>
            <span className="font-heading font-extrabold text-lg">BuzzerAI</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/troubleshooting" className="text-[#5C6B61] hover:text-[#121914]">Troubleshooting</Link>
            <Link to="/pricing" className="text-[#5C6B61] hover:text-[#121914]">Pricing</Link>
            <Link to="/signup" className="btn-primary !py-2 !px-4 text-sm">Start free</Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <div className="overline mb-3">Support · FAQ</div>
        <h1 className="text-4xl sm:text-5xl font-heading font-extrabold tracking-tight">Answers, straight up.</h1>
        <p className="text-[#5C6B61] mt-4 max-w-2xl leading-relaxed">
          The questions restaurant owners actually ask us on the first call. If yours isn't here, text us on{" "}
          <a href="mailto:hello@buzzerai.app" className="text-[#D96C5B] font-semibold">hello@buzzerai.app</a>{" "}
          and we'll add it.
        </p>
      </section>

      {/* FAQ list */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />
          ))}
        </div>
      </section>

      {/* CTA footer */}
      <section className="bg-[#1E3A2F] text-[#F7F6F2] px-6 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-extrabold mb-3">Still got questions?</h2>
          <p className="text-[#E1DFD5] mb-6 leading-relaxed">
            Start a free trial and see it work with your own restaurant's phone number. No credit card, no contract.
          </p>
          <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-[#D96C5B] text-white px-6 py-3 font-semibold hover:bg-[#c25b4c]" data-testid="faq-cta-signup">
            Start free trial →
          </Link>
        </div>
      </section>
    </div>
  );
}
